// @ts-check

import store from '@/store';
import { isNativeAppWebView } from 'utils/webview';
import setupI18n from '../setupI18n';

/**
 * Presentation mode the PayPal v6 session starts with.
 *
 * Inside the native app WebView the mode is always `modal`, whatever the whitelabel configures:
 * PayPal documents `modal` as the mode for WebViews, while `auto` tries a popup first — and in the
 * app a popup is handed to the host's `onOpenWindow`, which loads it over the funnel and kills the
 * SDK session. The browser (web and web-mobile) keeps the configured mode, `auto` by default.
 *
 * @param {string|undefined} configuredMode - `env.paypal.presentationMode` from the whitelabel.
 * @returns {string} The presentation mode to request.
 */
export function resolvePresentationMode(configuredMode) {
  if (isNativeAppWebView()) return 'modal';
  return configuredMode || 'auto';
}

/**
 * Gets the PayPal v6 configuration from whitelabel config.
 * @returns {Object} PayPal configuration object.
 */
export function getPayPalConfig() {
  const state = store.getState();
  const { whitelabelConfig } = state;
  const { env, features } = whitelabelConfig;
  const paypal = env?.paypal || {};

  const paypalConfig = {
    ...paypal,
    enabled: paypal.enabled && features?.PAYPAL_V6_ENABLED,
  };

  return paypalConfig;
}

/**
 * Checks if PayPal v6 is enabled for the current configuration.
 * @returns {boolean} True if PayPal v6 is enabled.
 */
export function isPayPalV6Enabled() {
  const config = getPayPalConfig();
  return Boolean(config.enabled);
}

/**
 * Gets the current application locale from i18n or whitelabel config.
 * @returns {string} Current application locale.
 */
export function getCurrentPayPalLocale() {
  let currentLocale;

  try {
    // Try to get current locale from i18n instance
    const i18n = setupI18n();
    currentLocale = i18n.language;
  } catch (error) {
    // If i18n is not available, fallback to whitelabel config
    console.warn('Could not get locale from i18n, falling back to whitelabel config:', error);
  }

  // Fallback to whitelabel config if i18n is not available
  if (!currentLocale) {
    const state = store.getState();
    currentLocale = state.whitelabelConfig?.env?.lang?.default;
  }

  // Final fallback
  return currentLocale || 'es-MX';
}

/**
 * Gets the currency code from whitelabel config.
 * @returns {string} Currency code (e.g., 'MXN', 'USD', 'BRL').
 */
export function getPayPalCurrency() {
  const state = store.getState();
  const currency = state.whitelabelConfig?.env?.currency;
  return currency || 'MXN';
}

/**
 * Checks if the current environment is sandbox.
 * @returns {boolean} True if sandbox environment.
 */
export function isPayPalSandbox() {
  const config = getPayPalConfig();
  return config.paypalRest?.environment ? config.paypalRest.environment === 'sandbox' : true;
}

/**
 * Gets the appropriate PayPal SDK URL based on environment.
 * @param {boolean} [sandbox=true] - Whether to use sandbox environment.
 * @returns {string} PayPal SDK URL.
 */
export function getPayPalSDKUrl(sandbox = true) {
  const config = getPayPalConfig();
  const sdkUrls = config.sdkUrls || {};

  if (sandbox) {
    return sdkUrls.sandbox || 'https://www.sandbox.paypal.com/web-sdk/v6/core';
  }

  return sdkUrls.production || 'https://www.paypal.com/web-sdk/v6/core';
}

/**
 * Gets complete PayPal v6 configuration for SDK initialization.
 * @returns {Object} Complete PayPal configuration object.
 */
export function getPayPalSDKConfig() {
  const config = getPayPalConfig();
  const sandbox = isPayPalSandbox();

  return {
    sandbox,
    presentationMode: resolvePresentationMode(config.presentationMode),
    locale: getCurrentPayPalLocale(),
    currency: getPayPalCurrency(),
    enabled: isPayPalV6Enabled(),
    sdkUrl: getPayPalSDKUrl(sandbox),
  };
}

/**
 * Validates PayPal v6 configuration.
 * @returns {Object} Validation result with isValid and errors.
 */
export function validatePayPalConfig() {
  const config = getPayPalConfig();
  const errors = [];

  if (!config.enabled) {
    errors.push('PayPal v6 is not enabled');
  }

  if (!config.sdkUrls?.sandbox && !config.sdkUrls?.production) {
    errors.push('PayPal SDK URLs are not configured');
  }

  if (!config.clientTokenEndpoint) {
    errors.push('PayPal client token endpoint is not configured');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export default {
  getPayPalConfig,
  isPayPalV6Enabled,
  getCurrentPayPalLocale,
  getPayPalCurrency,
  isPayPalSandbox,
  getPayPalSDKConfig,
  getPayPalSDKUrl,
  validatePayPalConfig,
};
